import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { PairTickers, PairTrend } from '@root/entities/pair/ui'

import { TradingPositionPrice } from './trading-position-price'
import { PositionSize } from './trading-position-size'

import type { AggregatedPositionModel } from '../../atoms'

type Props = {
  position: AggregatedPositionModel
  onClick?: (position: AggregatedPositionModel) => void
  active?: boolean
}

export const TradingPositionListItem: React.FC<Props> = ({
  position,
  onClick,
  active = false,
}) => {
  const haptics = useHapticFeedback()
  const [pnlUsd] = useAtom(position.pnlUsd)
  const [pnlNative] = useAtom(position.pnlNative)

  const { pair } = position
  const [entryMarketCapUsd] = useAtom(position.entryMarketCapUsd)
  const [entryPriceNative] = useAtom(position.entryPriceNative)

  const [targetMarketCapUsd] = useAtom(position.targetMarketCapUsd)
  const [targetPriceNative] = useAtom(position.targetPriceNative)

  const handleClick = () => {
    if (onClick) {
      haptics.impactOccurred('light')
      onClick(position)
    }
  }

  return (
    <Root onClick={handleClick} $active={active}>
      <IconContainer>
        <CurrencyIcon
          size={32}
          auxIconSize={14}
          withNetwork
          currency={pair.quote}
        />
      </IconContainer>
      <PairInformation>
        <PairInformationTop>
          <PairTickers
            quote={pair.quote}
            base={pair.base}
            premiumInfo={pair.premiumInfo}
          />
          <PairTrend
            showPercents
            changePercents={pnlUsd.pnlPercentage.toNumber()}
          />
        </PairInformationTop>
        <PositionSize
          pnlNative={pnlNative}
          pnlUsd={pnlUsd}
          pair={pair}
          position={position}
        />
      </PairInformation>
      <Group>
        <EntryContainer>
          <TradingPositionPrice
            type="entry"
            price={entryPriceNative}
            marketCap={entryMarketCapUsd}
            currency={CurrencyModel.nativeOf(position.network)}
          />
        </EntryContainer>
        <TargetContainer>
          <TradingPositionPrice
            type="target"
            price={targetPriceNative}
            marketCap={targetMarketCapUsd}
            currency={CurrencyModel.nativeOf(position.network)}
          />
        </TargetContainer>
      </Group>
    </Root>
  )
}

const Root = styled.div<{
  $active?: boolean
}>`
  height: 60px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  overflow: hidden;

  border-radius: 12px;
  border: 1px solid var(--backgroundA-2);

  background: var(--background-2);

  padding-inline: 10px;
  gap: 4px;

  &:active {
    background: var(--background-3);
  }

  ${({ $active }) => {
    if ($active) {
      return css`
        outline: 1px solid rgba(255, 255, 255, 0.56);
      `
    }
  }}
`

const Group = styled.div`
  display: grid;
  grid-template-columns: auto minmax(80px, auto);
  gap: 4px;
  margin-inline-start: 4px;
`

const IconContainer = styled.div`
  display: grid;
  padding-inline-end: 2px;
`

const PairInformation = styled.div`
  display: grid;
  justify-content: start;
`

const PairInformationTop = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  align-items: center;
  gap: 4px;
`

const EntryContainer = styled.div`
  display: grid;

  border-inline-end: 1px solid rgba(255, 255, 255, 0.05);
  padding-inline-end: 4px;
`

const TargetContainer = styled.div`
  display: grid;
`
